<script setup>
import { ref, computed } from "vue";
import { Link, router, usePage } from "@inertiajs/vue3";
import { Icon } from "@iconify/vue";
import Popover from "primevue/popover";
import BaseModalSimple from "@/Components/UI/modals/BaseModalSimple.vue";
import BaseButton from "@/Components/UI/buttons/BaseButton.vue";
import TeamSwitcher from "@/Components/UI/nav/TeamSwitcher.vue";
import notifyUser from "@/helpers/notifyUser.js";
import { useNavCollapsed } from "@/Composables/useNavCollapsed.js";
import { useNavSections } from "@/Composables/useNavSections.js";

const props = defineProps({
    currentType: {
        type: String,
        required: true,
    },
    isDesktop: {
        type: Boolean,
        default: false,
    },
    class: {
        default: "",
    },
});

defineEmits(["show-notifications"]);

const page = usePage();
const unavailableModal = ref(false);
const { collapsed, toggle: toggleCollapsed } = useNavCollapsed();
const { sections, toggle: toggleSection } = useNavSections();

// The icon-rail / flyout-popover mode is a desktop-only affordance. Below lg the
// menu only ever renders inside the mobile hamburger drawer, where it must stay
// fully expanded and labeled regardless of the persisted `collapsed` flag.
const railMode = computed(() => collapsed.value && props.isDesktop);

const popDocs = ref(null);
const popKB = ref(null);
const popSettings = ref(null);
const popServices = ref(null);

// Hide the default PrimeVue popover arrow on the collapsed-nav flyouts, and give
// the panel a roomier padding + larger radius so it reads as part of the design.
const navPopoverPt = Object.freeze({
    root: { class: "before:!hidden after:!hidden rounded-md" },
    content: { class: "!p-2" },
});

// ── Permission gates ──────────────────────────────────────────────────────────

const canViewKB = computed(
    () =>
        page.props.features?.knowledge_base &&
        page.props.permissions?.view?.knowledge_base_published &&
        !page.props.auth.teams?.current?.personal,
);

const canViewSettings = computed(
    () =>
        page.props.permissions.view.users ||
        page.props.permissions.view.subordinate_users ||
        page.props.permissions.view.document_templates ||
        page.props.permissions.view.logs ||
        page.props.permissions.view.team_ai_configure ||
        Boolean(page.props.auth.teams?.current),
);

// ── Active checks ─────────────────────────────────────────────────────────────

const isDocumentsActive = computed(() =>
    ["internal.documents", "internal.absences", "internal.employee_payments"].includes(props.currentType),
);
const isKBSectionActive = computed(() => props.currentType?.startsWith("knowledge"));
const isSettingsSectionActive = computed(() => props.currentType?.startsWith("settings"));
const isServicesSectionActive = computed(() =>
    ["public_sign", "public_sign_verify", "parsed_people"].includes(props.currentType),
);

function isActive(type) {
    if (type === "knowledge.base") return props.currentType === "knowledge.base";
    if (type === "knowledge.learning") return props.currentType === "knowledge.learning";
    if (type === "knowledge.reports") return page.url.startsWith("/knowledge/reports");
    return props.currentType === type;
}

// ── Sub-item data ─────────────────────────────────────────────────────────────

const documentsSubItems = computed(() => [
    page.props.permissions.view.documents && { type: "internal.documents", icon: "lucide:file-text", label: "Документы" },
    page.props.permissions.view.absences && { type: "internal.absences", icon: "lucide:calendar-days", label: "Отсутствия" },
    page.props.permissions.view.documents && { type: "internal.employee_payments", icon: "lucide:wallet", label: "Выплаты", desktopOnly: true },
].filter(Boolean));

const canViewLearning = computed(
    () =>
        page.props.permissions?.view?.learning_assignments_view_self ||
        page.props.permissions?.view?.learning_tracks_view ||
        page.props.permissions?.view?.learning_board_view_team,
);

const knowledgeSubItems = computed(() => [
    { type: "knowledge.base", icon: "lucide:book-open", label: "База знаний" },
    ...(canViewLearning.value
        ? [{
            type: "knowledge.learning",
            icon: "lucide:graduation-cap",
            label: "Обучение",
        }]
        : []),
    ...(page.props.permissions?.view?.report_view_any
        ? [{ type: "knowledge.reports", icon: "lucide:bar-chart-3", label: "Отчёты" }]
        : []),
]);

const settingsSubItems = computed(() => [
    (page.props.permissions.view.users || page.props.permissions.view.subordinate_users) && {
        type: "settings.structure", icon: "lucide:building", label: "Структура",
    },
    { type: "settings.documents", icon: "lucide:layout-template", label: "Настройки документов" },
    { type: "settings.organizations", icon: "lucide:building-2", label: "Организации" },
    page.props.permissions.view.logs && { type: "settings.logs", icon: "lucide:scroll-text", label: "Логи" },
    page.props.permissions.view.team_ai_configure && { type: "settings.team", icon: "lucide:cpu", label: "ИИ-провайдер" },
].filter(Boolean));

const servicesSubItems = computed(() => [
    { type: "public_sign", icon: "lucide:pen-line", label: "Подпись", href: route("public_sign") },
    { type: "public_sign_verify", icon: "lucide:shield-check", label: "Проверка подписи", href: route("public_signature_verification.page") },
    page.props.permissions.view.parsed_people && {
        type: "parsed_people", icon: "lucide:user-search", label: "Проверка сотрудников", href: route("parsed_people.index"),
    },
].filter(Boolean));

// ── Navigation ────────────────────────────────────────────────────────────────

function navigate(type) {
    const types = {
        "internal.documents": { route: route("documents.index"), permission: page.props.permissions.view.documents },
        "internal.absences": { route: route("absences.index"), permission: page.props.permissions.view.absences },
        "internal.employee_payments": { route: route("employee_payments.index"), permission: page.props.permissions.view.documents },
        "settings.structure": { route: route("users.index"), permission: page.props.permissions.view.users || page.props.permissions.view.subordinate_users },
        "settings.documents": { route: route("documents.templates.index"), permission: page.props.permissions.view.document_templates },
        "settings.logs": { route: route("logs.index"), permission: page.props.permissions.view.logs },
        "settings.organizations": { route: route("organizations.index"), permission: Boolean(page.props.auth.teams?.current) },
        "settings.team": { route: route("settings.team.show"), permission: page.props.permissions.view.team_ai_configure },
        // KB routes only exist in Ziggy when the feature is enabled; resolving
        // route("knowledge.*") otherwise throws and breaks every nav click.
        ...(page.props.features?.knowledge_base
            ? {
                "knowledge.base": { route: route("knowledge.index"), permission: true },
                // «Обучение» lands on Мои назначения when available, otherwise Программы.
                "knowledge.learning": {
                    route: page.props.permissions?.view?.learning_assignments_view_self
                        ? route("knowledge.assignments.index")
                        : route("knowledge.tracks.index"),
                    permission: canViewLearning.value,
                },
                "knowledge.reports": { route: route("knowledge.reports.index"), permission: page.props.permissions?.view?.report_view_any },
            }
            : {}),
    };

    const item = types[type];
    if (!item) return;

    if (item.permission) {
        router.visit(item.route);
        return;
    }

    if (!page.props.auth.teams?.current) {
        router.visit(item.route);
        return;
    }

    if (page.props.permissions.view.payments) {
        unavailableModal.value = true;
    } else {
        notifyUser("Недостаточно прав для просмотра");
    }
}

function navFromPop(type, pop) {
    pop?.hide();
    navigate(type);
}

function buy() {
    if (page.props.auth.teams?.current) {
        window.location.href = "https://dobyto.ru/";
        return;
    }
    router.post(route("teams.personal.create"));
}
</script>

<template>
    <BaseModalSimple
        :show="unavailableModal"
        class="text-center"
        @close="unavailableModal = false">
        <template #title>Купите полный доступ</template>
        <template #description>
            <p class="mt-3">Для доступа к этой функциональности требуется оплата.</p>
        </template>
        <template #content>
            <BaseButton variant="primary" class="w-full mt-6" @click="buy">Купить</BaseButton>
        </template>
    </BaseModalSimple>

    <nav
        :class="[props.class, railMode ? 'w-28 px-2' : (isDesktop ? 'w-128 px-10' : 'w-screen md:w-128 px-10')]"
        class="fixed py-8 z-40 border-r border-light-2 text-sm font-medium mt-32 h-nav-left bg-white flex flex-col justify-between transition-[width,padding] duration-300 ease-in-out motion-reduce:transition-none overflow-hidden">

        <Transition name="kb-nav-fade" mode="out-in">
        <!-- ══════════════════ COLLAPSED (desktop icon rail) ══════════════════ -->
        <div v-if="railMode" class="flex flex-col gap-2">

            <!-- Документы -->
            <div class="relative">
                <button
                    v-tooltip.right="'Документы'"
                    aria-label="Документы"
                    :class="isDocumentsActive ? 'bg-blue-5 text-accent-blue' : 'text-dark-2 hover:bg-blue-5 hover:text-dark-1'"
                    class="relative flex items-center justify-center w-full min-h-22 rounded transition duration-200"
                    @click="popDocs?.toggle($event)">
                    <Icon icon="lucide:file-text" class="w-10 h-10" />
                </button>
                <Popover ref="popDocs" :pt="navPopoverPt">
                    <div class="py-1 min-w-56">
                        <div class="flex items-center gap-3 px-5 pt-3 pb-2 text-dark-light">
                            <Icon icon="lucide:file-text" class="w-8 h-8 flex-shrink-0" />
                            <span class="text-base font-semibold leading-none">Документы</span>
                        </div>
                        <button
                            v-for="item in documentsSubItems"
                            :key="item.type"
                            :class="isActive(item.type) ? 'bg-blue-5 text-accent-blue font-medium' : 'text-dark-1 hover:bg-blue-5 hover:text-dark-1'"
                            class="flex items-center w-full pl-16 pr-5 py-4 text-sm rounded-md transition duration-200"
                            @click="navFromPop(item.type, popDocs)">
                            {{ item.label }}
                        </button>
                    </div>
                </Popover>
            </div>

            <!-- База знаний -->
            <div v-if="canViewKB" class="relative">
                <button
                    v-tooltip.right="'База знаний'"
                    aria-label="База знаний"
                    :class="isKBSectionActive ? 'bg-blue-5 text-accent-blue' : 'text-dark-2 hover:bg-blue-5 hover:text-dark-1'"
                    class="relative flex items-center justify-center w-full min-h-22 rounded transition duration-200"
                    @click="popKB?.toggle($event)">
                    <Icon icon="lucide:book-open" class="w-10 h-10" />
                </button>
                <Popover ref="popKB" :pt="navPopoverPt">
                    <div class="py-1 min-w-56">
                        <div class="flex items-center gap-3 px-5 pt-3 pb-2 text-dark-light">
                            <Icon icon="lucide:book-open" class="w-8 h-8 flex-shrink-0" />
                            <span class="text-base font-semibold leading-none">База знаний</span>
                        </div>
                        <button
                            v-for="item in knowledgeSubItems"
                            :key="item.type"
                            :class="isActive(item.type) ? 'bg-blue-5 text-accent-blue font-medium' : 'text-dark-1 hover:bg-blue-5 hover:text-dark-1'"
                            class="flex items-center gap-5 w-full pl-16 pr-5 py-4 text-sm rounded-md transition duration-200"
                            @click="navFromPop(item.type, popKB)">
                            <span class="flex-1 text-left">{{ item.label }}</span>
                            <span
                                v-if="item.badge"
                                class="min-w-7 h-7 px-1 inline-flex items-center justify-center rounded-full bg-accent-orange text-white text-2xs font-semibold tabular-nums">
                                {{ item.badge > 99 ? "99+" : item.badge }}
                            </span>
                        </button>
                    </div>
                </Popover>
            </div>

            <!-- Настройки -->
            <div v-if="canViewSettings" class="relative">
                <button
                    v-tooltip.right="'Настройки'"
                    aria-label="Настройки"
                    :class="isSettingsSectionActive ? 'bg-blue-5 text-accent-blue' : 'text-dark-2 hover:bg-blue-5 hover:text-dark-1'"
                    class="relative flex items-center justify-center w-full min-h-22 rounded transition duration-200"
                    @click="popSettings?.toggle($event)">
                    <Icon icon="lucide:settings" class="w-10 h-10" />
                </button>
                <Popover ref="popSettings" :pt="navPopoverPt">
                    <div class="py-1 min-w-56">
                        <div class="flex items-center gap-3 px-5 pt-3 pb-2 text-dark-light">
                            <Icon icon="lucide:settings" class="w-8 h-8 flex-shrink-0" />
                            <span class="text-base font-semibold leading-none">Настройки</span>
                        </div>
                        <button
                            v-for="item in settingsSubItems"
                            :key="item.type"
                            :class="isActive(item.type) ? 'bg-blue-5 text-accent-blue font-medium' : 'text-dark-1 hover:bg-blue-5 hover:text-dark-1'"
                            class="flex items-center w-full pl-16 pr-5 py-4 text-sm rounded-md transition duration-200"
                            @click="navFromPop(item.type, popSettings)">
                            {{ item.label }}
                        </button>
                    </div>
                </Popover>
            </div>

            <!-- Сервисы -->
            <div class="relative">
                <button
                    v-tooltip.right="'Сервисы'"
                    aria-label="Сервисы"
                    :class="isServicesSectionActive ? 'bg-blue-5 text-accent-blue' : 'text-dark-2 hover:bg-blue-5 hover:text-dark-1'"
                    class="relative flex items-center justify-center w-full min-h-22 rounded transition duration-200"
                    @click="popServices?.toggle($event)">
                    <img src="/storage/images/logo-small.svg" class="w-10 h-10" alt="" aria-hidden="true" />
                </button>
                <Popover ref="popServices" :pt="navPopoverPt">
                    <div class="py-1 min-w-56">
                        <div class="flex items-center gap-3 px-5 pt-3 pb-2 text-dark-light">
                            <img src="/storage/images/logo-small.svg" class="w-8 h-8 flex-shrink-0" alt="" aria-hidden="true" />
                            <span class="text-base font-semibold leading-none">Сервисы</span>
                        </div>
                        <Link
                            v-for="item in servicesSubItems"
                            :key="item.type"
                            :href="item.href"
                            :class="isActive(item.type) ? 'bg-blue-5 text-accent-blue font-medium' : 'text-dark-1 hover:bg-blue-5 hover:text-dark-1'"
                            class="flex items-center w-full pl-16 pr-5 py-4 text-sm rounded-md transition duration-200"
                            @click="popServices?.hide()">
                            {{ item.label }}
                        </Link>
                    </div>
                </Popover>
            </div>
        </div>

        <!-- ══════════════════ EXPANDED ══════════════════ -->
        <div v-else class="flex flex-col gap-6 overflow-y-auto min-h-0">
            <!-- `flex`, not `block`: this class reaches the trigger BaseButton via
                 TeamSwitcher's $attrs, and BaseButton puts caller classes last in
                 cn(), so `block` beat its own `flex` and stacked the icon, label
                 and chevron on mobile. -->
            <TeamSwitcher class="flex md:hidden" />

            <!-- ── Документы ── -->
            <div>
                <button
                    class="flex items-center justify-between w-full px-5 pb-3 text-sm font-semibold text-dark-2 hover:text-dark-1 transition duration-200"
                    @click="toggleSection('documents')">
                    <span class="flex items-center gap-3">
                        <Icon icon="lucide:file-text" class="w-8 h-8 flex-shrink-0" />
                        Документы
                    </span>
                    <Icon :icon="sections.documents ? 'lucide:chevron-down' : 'lucide:chevron-right'" class="w-6 h-6" />
                </button>
                <div v-show="sections.documents">
                    <button
                        v-for="item in documentsSubItems"
                        :key="item.type"
                        :class="[
                            isActive(item.type) ? 'bg-blue-5 text-accent-blue font-semibold' : 'text-dark-light hover:bg-blue-5 hover:text-dark-1',
                            item.desktopOnly ? 'hidden md:flex' : 'flex',
                        ]"
                        class="items-center w-full pl-16 pr-5 py-3 rounded transition duration-200 text-left"
                        @click="navigate(item.type)">
                        <span>{{ item.label }}</span>
                    </button>
                </div>
            </div>

            <!-- ── База знаний ── -->
            <div v-if="canViewKB">
                <button
                    class="flex items-center justify-between w-full px-5 pb-3 text-sm font-semibold text-dark-2 hover:text-dark-1 transition duration-200"
                    @click="toggleSection('knowledge')">
                    <span class="flex items-center gap-3">
                        <Icon icon="lucide:book-open" class="w-8 h-8 flex-shrink-0" />
                        База знаний
                    </span>
                    <Icon :icon="sections.knowledge ? 'lucide:chevron-down' : 'lucide:chevron-right'" class="w-6 h-6" />
                </button>
                <div v-show="sections.knowledge">
                    <button
                        v-for="item in knowledgeSubItems"
                        :key="item.type"
                        :class="isActive(item.type) ? 'bg-blue-5 text-accent-blue font-semibold' : 'text-dark-light hover:bg-blue-5 hover:text-dark-1'"
                        class="flex items-center gap-5 w-full pl-16 pr-5 py-3 rounded transition duration-200 text-left"
                        @click="navigate(item.type)">
                        <span class="flex-1 text-left">{{ item.label }}</span>
                        <span
                            v-if="item.badge"
                            class="min-w-7 h-7 px-1 inline-flex items-center justify-center rounded-full bg-accent-orange text-white text-2xs font-semibold tabular-nums">
                            {{ item.badge > 99 ? "99+" : item.badge }}
                        </span>
                    </button>
                </div>
            </div>

            <!-- ── Настройки ── · desktop-only (hidden on mobile, matching stage) -->
            <div v-if="canViewSettings" class="hidden md:block">
                <button
                    class="flex items-center justify-between w-full px-5 pb-3 text-sm font-semibold text-dark-2 hover:text-dark-1 transition duration-200"
                    @click="toggleSection('settings')">
                    <span class="flex items-center gap-3">
                        <Icon icon="lucide:settings" class="w-8 h-8 flex-shrink-0" />
                        Настройки
                    </span>
                    <Icon :icon="sections.settings ? 'lucide:chevron-down' : 'lucide:chevron-right'" class="w-6 h-6" />
                </button>
                <div v-show="sections.settings">
                    <button
                        v-for="item in settingsSubItems"
                        :key="item.type"
                        :class="isActive(item.type) ? 'bg-blue-5 text-accent-blue font-semibold' : 'text-dark-light hover:bg-blue-5 hover:text-dark-1'"
                        class="flex items-center w-full pl-16 pr-5 py-3 rounded transition duration-200 text-left"
                        @click="navigate(item.type)">
                        <span>{{ item.label }}</span>
                    </button>
                </div>
            </div>

            <!-- ── Сервисы ── · desktop-only (hidden on mobile, matching stage) -->
            <div class="hidden md:block">
                <button
                    class="flex items-center justify-between w-full px-5 pb-3 text-sm font-semibold text-dark-2 hover:text-dark-1 transition duration-200"
                    @click="toggleSection('services')">
                    <span class="flex items-center gap-3">
                        <img src="/storage/images/logo-small.svg" class="w-8 h-8 flex-shrink-0" alt="" aria-hidden="true" />
                        Сервисы
                    </span>
                    <Icon :icon="sections.services ? 'lucide:chevron-down' : 'lucide:chevron-right'" class="w-6 h-6" />
                </button>
                <div v-show="sections.services">
                    <Link
                        v-for="item in servicesSubItems"
                        :key="item.type"
                        :href="item.href"
                        :class="isActive(item.type) ? 'bg-blue-5 text-accent-blue font-semibold' : 'text-dark-light hover:bg-blue-5 hover:text-dark-1'"
                        class="flex items-center w-full pl-16 pr-5 py-3 rounded transition duration-200">
                        <span>{{ item.label }}</span>
                    </Link>
                </div>
            </div>
        </div>
        </Transition>

        <!-- ══════════════════ BOTTOM ══════════════════ -->
        <div :class="railMode ? 'flex flex-col gap-1' : 'border-t border-light-2 pt-5 flex flex-col gap-1'">
            <!-- Toggle collapse — desktop-only affordance, hidden in the mobile drawer -->
            <button
                v-if="isDesktop"
                :key="railMode ? 'nav-toggle-collapsed' : 'nav-toggle-expanded'"
                v-tooltip.right="railMode ? 'Развернуть меню' : ''"
                :aria-label="railMode ? 'Развернуть меню' : 'Свернуть меню'"
                :class="railMode ? 'justify-center' : 'px-5 gap-5'"
                class="flex items-center w-full min-h-22 rounded text-dark-2 hover:bg-blue-5 hover:text-dark-1 transition duration-200"
                @click="toggleCollapsed">
                <Icon :icon="railMode ? 'lucide:panel-left-open' : 'lucide:panel-left-close'" class="w-8 h-8 flex-shrink-0" />
                <span v-if="!railMode">Свернуть</span>
            </button>

            <!-- Logout -->
            <Link
                :href="route('logout')"
                :class="railMode ? 'justify-center' : 'px-5 gap-5'"
                class="flex w-full items-center min-h-22 transition duration-200 hover:bg-red-5 cursor-pointer rounded text-accent-red"
                method="POST"
                as="button"
                aria-label="Выход"
                v-tooltip.right="railMode ? 'Выход' : ''">
                <Icon icon="lucide:log-out" class="w-8 h-8 flex-shrink-0" />
                <span v-if="!railMode">Выход</span>
            </Link>
        </div>
    </nav>
</template>

<style scoped>
.h-nav-left {
    height: calc(100vh - 4rem);
}

.kb-nav-fade-enter-active,
.kb-nav-fade-leave-active {
    transition: opacity 180ms ease-out;
}

.kb-nav-fade-enter-from,
.kb-nav-fade-leave-to {
    opacity: 0;
}
</style>
